// src/components/Chat/shared/ReplyQuote.tsx
// Box de quote dentro da mensagem mostrando a mensagem original respondida

import { motion } from "framer-motion";
import { Ban, FileText, Image, Music } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ReplyToMessage } from "@/types/chat";

interface ReplyQuoteProps {
  replyTo: ReplyToMessage;
  onClick?: () => void;
  isOwnMessage?: boolean;
}

export function ReplyQuote({
  replyTo,
  onClick,
  isOwnMessage = false,
}: ReplyQuoteProps) {
  const { t } = useTranslation();

  // Truncar conteúdo
  const truncateContent = (content: string | null, maxLength = 80) => {
    if (!content) return "";
    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  // Verificar se tem arquivos
  const getFileInfo = () => {
    if (!replyTo.files || replyTo.files.length === 0) return null;

    const file = replyTo.files[0];
    const fileName =
      replyTo.file_names?.[0] || file.displayName || file.originalName;

    // Detectar tipo
    const extension = fileName?.toLowerCase().split(".").pop() || "";
    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
    const isAudio = ["mp3", "wav", "ogg", "m4a", "webm"].includes(extension);

    return { fileName, isImage, isAudio, count: replyTo.files.length };
  };

  const fileInfo = getFileInfo();

  // Mensagem deletada
  if (replyTo.deleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`mb-2 rounded-lg p-2 cursor-default ${
          isOwnMessage
            ? "bg-blue-400/30 border-l-2 border-white/50"
            : "bg-gray-200/50 border-l-2 border-gray-400"
        }`}
      >
        <div className="flex items-center gap-1.5 text-xs opacity-70">
          <Ban size={12} />
          <span className="italic">
            {t("chatFeatures.reply.messageDeleted")}
          </span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClick}
      className={`mb-2 rounded-lg p-2 cursor-pointer transition-all hover:brightness-95 ${
        isOwnMessage
          ? "bg-blue-400/30 border-l-2 border-white/70"
          : "bg-gray-200/70 border-l-2 border-blue-500"
      }`}
    >
      {/* Nome do autor original */}
      <p
        className={`text-xs font-semibold mb-0.5 ${
          isOwnMessage ? "text-white/90" : "text-blue-600"
        }`}
      >
        {replyTo.sender_name || "Usuário"}
      </p>

      {/* Conteúdo da mensagem original */}
      {replyTo.content && (
        <p
          className={`text-xs truncate ${
            isOwnMessage ? "text-white/80" : "text-gray-600"
          }`}
        >
          {truncateContent(replyTo.content)}
        </p>
      )}

      {/* Info de arquivo se houver */}
      {fileInfo && (
        <div
          className={`flex items-center gap-1 text-xs mt-0.5 ${
            isOwnMessage ? "text-white/70" : "text-gray-500"
          }`}
        >
          {fileInfo.isImage && <Image size={12} />}
          {fileInfo.isAudio && <Music size={12} />}
          {!fileInfo.isImage && !fileInfo.isAudio && <FileText size={12} />}
          <span className="truncate">
            {fileInfo.fileName}
            {fileInfo.count > 1 && ` +${fileInfo.count - 1}`}
          </span>
        </div>
      )}
    </motion.div>
  );
}

export default ReplyQuote;
